import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

const PBKDF2_ITERATIONS = 1000;
const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const KEY_LENGTH = 32;
const TAG_LENGTH = 128; // bits (16 bytes)

/**
 * Cifrado coherente con CertifirmaAgent y Canal_Ventas/backs Java.
 * AES-256-GCM; clave derivada con PBKDF2-HMAC-SHA256, 1000 iteraciones, 32 bytes.
 * Blob (antes de Base64): [ IV 12 bytes ][ Salt 16 bytes ][ Ciphertext ][ Tag 16 bytes ].
 */
@Injectable({
  providedIn: 'root'
})
export class CipherService {

  constructor() {}

  /** Indica si el cifrado está activo (clave configurada). */
  get isEncryptionEnabled(): boolean {
    const token = environment.cipher;
    return typeof token === 'string' && token.length > 0;
  }

  /**
   * Cifra un texto en claro y devuelve el blob en Base64.
   * Formato del blob: IV(12) + Salt(16) + ciphertext + tag(16).
   */
  async encrypt(plainText: string): Promise<string> {
    const token = environment.cipher;
    if (!token || typeof token !== 'string') {
      throw new Error('CipherService: cipher no configurado en environment');
    }
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const key = await this.deriveKey(token, salt);
    const encoded = new TextEncoder().encode(plainText);
    const ciphertextWithTag = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
        tagLength: TAG_LENGTH
      },
      key,
      encoded
    );
    const blob = this.concat(
      iv,
      salt,
      new Uint8Array(ciphertextWithTag)
    );
    return this.uint8ArrayToBase64(blob);
  }

  /**
   * Descifra un blob en Base64 y devuelve el texto en claro.
   */
  async decrypt(base64CipherText: string): Promise<string> {
    const token = environment.cipher;
    if (!token || typeof token !== 'string') {
      throw new Error('CipherService: cipher no configurado en environment');
    }
    const blob = this.base64ToUint8Array(base64CipherText);
    if (blob.length < IV_LENGTH + SALT_LENGTH + 16) {
      throw new Error('CipherService: blob cifrado demasiado corto');
    }
    const iv = blob.slice(0, IV_LENGTH);
    const salt = blob.slice(IV_LENGTH, IV_LENGTH + SALT_LENGTH);
    const ciphertextWithTag = blob.slice(IV_LENGTH + SALT_LENGTH);
    const key = await this.deriveKey(token, salt);
    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
        tagLength: TAG_LENGTH
      },
      key,
      ciphertextWithTag
    );
    return new TextDecoder().decode(decrypted);
  }

  private async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const enc = new TextEncoder().encode(password);
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      enc,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: KEY_LENGTH * 8 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  private concat(...arrays: Uint8Array[]): Uint8Array {
    const total = arrays.reduce((acc, a) => acc + a.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const a of arrays) {
      out.set(a, offset);
      offset += a.length;
    }
    return out;
  }

  private uint8ArrayToBase64(bytes: Uint8Array): string {
    const chunkSize = 8192;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
      binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
    }
    return btoa(binary);
  }

  private base64ToUint8Array(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
